import React from 'react';
import clsx from 'clsx';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { EditorContent, useEditor } from '@tiptap/react';
import type { Editor as EditorType } from '@tiptap/react';

import { EDITOR_SCOPE_ID } from '~/components/document-editor/editor-css';
import { extractBodyContent } from '~/components/document-editor/template';
import { useEditorContentSync } from '~/components/document-editor/use-editor-content-sync';
import {
  type EditorSaveStatus,
  useEditorDocumentPersistence
} from '~/components/document-editor/use-editor-document-persistence';
import {
  useEditorImageSetup,
  type EditorResizeControlsState
} from '~/components/document-editor/use-editor-image-setup';
import { setPageBreaks } from './extensions/page-guide';
import { countPageBreaks, usePageBreaks } from '~/components/workspaces/hooks/use-page-breaks';
import { useEditorPreview } from '~/components/document-editor/use-editor-preview';
import { useEditorStylesheets } from '~/components/document-editor/use-editor-stylesheets';
import { usePageFurniture } from '~/components/workspaces/hooks/use-page-furniture';
import { setMeasuredPages, setPageFurniture, pageDimensions } from './extensions/page-furniture';
import {
  type CommentHighlight,
  revealComment,
  setCommentHighlights
} from './extensions/comment-highlight';
import {
  type SelectionCandidate,
  selectionCandidate
} from '~/components/workspaces/lib/selection-candidate';
import { ImageResizeControls } from '~/components/document-editor/editor-image-resize-controls';
import { buildEditorExtensions } from './build-editor-extensions';
import type { MarkedRegion, PageRegion } from '~/components/pdf-pages-viewer';
import { MenuBar } from './editor-menu-bar';
import { EditorPreviewPane } from './editor-preview-pane';
import { usePagedDocumentsEnabled } from '~/components/use-paged-documents-enabled';
import { HtmlDocumentEditor as LegacyHtmlDocumentEditor } from '~/pages/copilot/workspace/legacy-editor/html-document-editor';

export type HtmlDocumentEditorProps = {
  content: string;
  editable: boolean;
  onUpdate: (content: string) => void | Promise<void>;
  onSaveStatusChange?: (status: EditorSaveStatus) => void;
  /** Offered on a non-empty selection; the selection travels as a candidate for an anchor. */
  onCommentSelection?: (candidate: SelectionCandidate) => void;
  commentHighlights?: CommentHighlight[];
  activeCommentId?: string | null;
  /** A request to select a comment's text; the nonce lets the same comment be revealed twice. */
  revealComment?: { highlight: CommentHighlight; nonce: number } | null;
  workspaceId?: string;
  templateId?: string;
  /** Unsaved Design selection for a Library template; null previews the agency default. */
  templateDesignId?: string | null;
  artifactId?: string;
  artifactVersion?: number;
  artifactRevision?: string | number;
  showPreview?: boolean;
  onPreviewToggle?: () => void;
  /** Offered in place of Edit while the version shown is not the document's latest. */
  onOpenLatest?: () => void;
  /** Shown in the pages and editor bars to explain why the document is read-only. */
  readOnlyNote?: string;
  /** The pages the document declares and the pages its render printed, once a render answers. */
  onPageCount?: (pages: { declared: number; printed: number }) => void;
  showImageButton?: boolean;
  /**
   * `bare` drops the frame and the writing area's own scrollbar, and pins the
   * toolbar to the top of whatever is scrolling outside it. It is for the mail
   * composer, where the header's own hairline already bounds the body: a
   * scrollable editor inside a scrollable page gives the reader two bars that
   * mean different amounts of the same letter, and neither one measures it.
   */
  chrome?: 'framed' | 'bare';
  /** Comment boxes to draw over the preview pages, and how a new one is drawn. */
  commentRegions?: MarkedRegion[];
  onDrawCommentRegion?: (region: PageRegion) => void;
  onCommentRegionClick?: (id: string) => void;
};

/**
 * TipTap editor for workspace HTML document artifacts and, with designs on, saved Library
 * templates. Agencies without the paged document get main's editor, which ignores the comment
 * and revision props. The Library switches between the two itself: a template with no id yet
 * keeps main's editor even with designs on. The email composer and signatures import main's
 * editor directly whatever the flags.
 *
 * A template has no workspace artifact, so its page breaks and page furniture are never
 * requested and the pages are drawn without measured guides.
 */
export const HtmlDocumentEditor = (props: HtmlDocumentEditorProps) =>
  usePagedDocumentsEnabled() ? (
    <PagedHtmlDocumentEditor {...props} />
  ) : (
    <LegacyHtmlDocumentEditor {...props} />
  );

/**
 * Composes toolbar, preview, extensions, save/sync, and stylesheet injection.
 */
const PagedHtmlDocumentEditor = ({
  content,
  editable,
  onUpdate,
  onSaveStatusChange,
  onCommentSelection,
  commentHighlights,
  activeCommentId,
  revealComment: revealRequest,
  workspaceId,
  templateId,
  templateDesignId,
  artifactId,
  artifactVersion,
  artifactRevision,
  showPreview,
  onPreviewToggle,
  onOpenLatest,
  readOnlyNote,
  onPageCount,
  showImageButton = true,
  chrome = 'framed',
  commentRegions,
  onDrawCommentRegion,
  onCommentRegionClick
}: HtmlDocumentEditorProps) => {
  const editorRootRef = React.useRef<HTMLDivElement | null>(null);
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const [resizeControlsState, setResizeControlsState] =
    React.useState<EditorResizeControlsState>(null);

  const lastCanceledKeyRef = React.useRef<string | undefined>(
    artifactId !== undefined ? `${artifactId}:${artifactVersion ?? ''}` : undefined
  );

  const identity = JSON.stringify([workspaceId, artifactId, artifactVersion, templateId]);
  const [renderSource, setRenderSource] = React.useState({
    identity,
    revision: artifactRevision,
    pageBreakRevision: 0
  });
  const savedPageBreaksRef = React.useRef(countPageBreaks(content));
  if (renderSource.identity !== identity || renderSource.revision !== artifactRevision) {
    setRenderSource({ ...renderSource, identity, revision: artifactRevision });
    savedPageBreaksRef.current = countPageBreaks(content);
  }
  const renderRevision = `${renderSource.revision ?? 0}:${renderSource.pageBreakRevision}`;
  const onSaved = React.useCallback((saved: string) => {
    const breaks = countPageBreaks(saved);
    if (breaks !== savedPageBreaksRef.current) {
      setRenderSource((current) => ({
        ...current,
        pageBreakRevision: current.pageBreakRevision + 1
      }));
    }
    savedPageBreaksRef.current = breaks;
  }, []);

  // The editor edits the stored document. Renders only draw page guides over it: loading a
  // render's rewrite would save the renderer's page structure with the reader's next keystroke.
  const persistence = useEditorDocumentPersistence({
    content,
    onUpdate,
    onSaveStatusChange,
    onSaved
  });

  const { onCreate } = useEditorImageSetup({
    workspaceId,
    templateId,
    artifactId,
    setResizeControlsState
  });

  // TipTap only edits body HTML; avoid seeding full documents (content sync applies the prop).
  const initialEditorContent = React.useMemo(() => extractBodyContent(content), []);

  const editor = useEditor(
    {
      editable,
      extensions: buildEditorExtensions(EDITOR_SCOPE_ID),
      content: initialEditorContent,
      onUpdate: ({ editor: ed, transaction }) => {
        if (
          !ed.isEditable ||
          !transaction.docChanged ||
          ed.getHTML() === persistence.lastContentRef.current ||
          persistence.isInitialMountRef.current
        ) {
          return;
        }

        const newContent = ed.getHTML();
        const candidateToSave = persistence.getContentToSave(newContent);
        if (
          candidateToSave === persistence.lastSavedContentRef.current &&
          persistence.pendingSavesRef.current === 0
        ) {
          persistence.cancelPendingSave();
          persistence.lastContentRef.current = newContent;
          return;
        }

        persistence.lastContentRef.current = newContent;
        persistence.debouncedOnUpdate(newContent);
      },
      onBlur: async ({ editor: ed }) => {
        const currentHTML = ed.getHTML();
        if (
          !ed.isEditable ||
          (currentHTML === persistence.lastContentRef.current &&
            !persistence.needsRetryRef.current &&
            !persistence.hasUnsentEdit())
        ) {
          return;
        }
        persistence.lastContentRef.current = currentHTML;
        await persistence.flushSave(currentHTML);
      },
      onCreate
    },
    []
  );

  useEditorContentSync({
    editor,
    focusScopeRef: panelRef,
    content,
    artifactId,
    getContentToSave: persistence.getContentToSave,
    documentPrefixRef: persistence.documentPrefixRef,
    documentSuffixRef: persistence.documentSuffixRef,
    lastContentRef: persistence.lastContentRef,
    lastSavedContentRef: persistence.lastSavedContentRef,
    isInitialMountRef: persistence.isInitialMountRef,
    awaitingSaveEchoRef: persistence.awaitingSaveEchoRef,
    pendingSavesRef: persistence.pendingSavesRef,
    savesSettled: persistence.savesSettled
  });

  const { leaveDocument } = persistence;

  React.useEffect(() => {
    const key = artifactId !== undefined ? `${artifactId}:${artifactVersion ?? ''}` : undefined;
    if (key === lastCanceledKeyRef.current) return;
    lastCanceledKeyRef.current = key;
    leaveDocument();
  }, [artifactId, artifactVersion, leaveDocument]);

  useEditorStylesheets({
    editorRootRef,
    content,
    scopeId: EDITOR_SCOPE_ID,
    workspaceId,
    artifactId,
    artifactVersion,
    artifactRevision: renderRevision,
    templateId,
    templateDesignId,
    showPreview
  });

  const {
    boundaries: pageBreaks,
    pageCount,
    declaredPageCount
  } = usePageBreaks({
    enabled: !showPreview,
    workspaceId,
    artifactId,
    artifactVersion,
    templateId,
    revision: renderRevision
  });

  React.useEffect(() => {
    if (editor) setPageBreaks(editor as EditorType, pageBreaks);
  }, [editor, pageBreaks]);

  React.useEffect(() => {
    if (pageCount > 0) onPageCount?.({ declared: declaredPageCount, printed: pageCount });
  }, [onPageCount, pageCount, declaredPageCount]);

  React.useEffect(() => {
    if (!editor) return;
    setMeasuredPages(
      editor as EditorType,
      pageBreaks ? { pageCount, boundaries: pageBreaks } : null
    );
  }, [editor, pageBreaks, pageCount]);

  const furniture = usePageFurniture({
    enabled: !showPreview,
    workspaceId,
    artifactId,
    artifactVersion,
    artifactRevision: renderRevision,
    templateId
  });

  React.useEffect(() => {
    if (editor) setPageFurniture(editor as EditorType, furniture);
  }, [editor, furniture]);

  const preview = useEditorPreview({
    showPreview,
    workspaceId,
    artifactId,
    artifactVersion,
    artifactRevision: renderRevision,
    templateId
  });

  React.useEffect(() => {
    editor?.setEditable(editable);
  }, [editable, editor]);

  React.useEffect(() => {
    if (editor)
      setCommentHighlights(editor as EditorType, commentHighlights ?? [], activeCommentId ?? null);
  }, [editor, commentHighlights, activeCommentId]);

  React.useEffect(() => {
    if (editor && revealRequest) revealComment(editor as EditorType, revealRequest.highlight);
  }, [editor, revealRequest]);

  // The Comment affordance follows the selection. It is positioned against the scrolling
  // panel, which is the nearest positioned ancestor, so it scrolls with the page.
  const [commentOffer, setCommentOffer] = React.useState<{ x: number; y: number } | null>(null);
  React.useEffect(() => {
    if (!editor || !onCommentSelection) return;
    const place = () => {
      const panel = panelRef.current;
      if (
        !panel ||
        !editor.isEditable ||
        editor.state.selection.empty ||
        !selectionCandidate(editor as EditorType)
      ) {
        setCommentOffer(null);
        return;
      }
      const coords = editor.view.coordsAtPos(editor.state.selection.to);
      const rect = panel.getBoundingClientRect();
      setCommentOffer({
        x: coords.left - rect.left + panel.scrollLeft,
        y: coords.bottom - rect.top + panel.scrollTop + 6
      });
    };
    editor.on('selectionUpdate', place);
    editor.on('blur', place);
    return () => {
      editor.off('selectionUpdate', place);
      editor.off('blur', place);
    };
  }, [editor, onCommentSelection]);

  const showPreviewPane = Boolean(showPreview && preview.previewUrl);
  const bare = chrome === 'bare';

  const menuBar = (
    <MenuBar
      editor={editor as EditorType}
      editable={editable}
      readOnlyNote={readOnlyNote}
      onOpenLatest={onOpenLatest}
      showPreview={showPreview}
      onPreviewToggle={onPreviewToggle}
      showImageButton={showImageButton}
      subtle={bare}
    />
  );

  return (
    <div
      ref={panelRef}
      className={clsx(
        'relative flex flex-col flex-grow',
        // Bare grows with its content and lets the page scroll. `overflow-x`
        // is left alone rather than hidden: hiding one axis makes the other
        // `auto`, which would hand the writing area a scrollbar again and clip
        // the toolbar that is meant to stick to the page.
        !bare &&
          'mt-2 min-h-0 overflow-y-auto overflow-x-auto rounded-lg border border-neutral-200 dark:border-neutral-700'
      )}
    >
      <div
        className={clsx(
          'sticky top-0 left-0 z-10 w-full bg-white dark:bg-neutral-800',
          bare ? 'border-b border-neutral-200 dark:border-neutral-700' : 'shadow-sm'
        )}
      >
        {showPreviewPane ? (
          <PreviewBar
            onEdit={editable ? onPreviewToggle : undefined}
            onOpenLatest={onOpenLatest}
            note={readOnlyNote}
          />
        ) : (
          menuBar
        )}
      </div>

      {showPreviewPane && preview.previewUrl ? (
        <EditorPreviewPane
          previewUrl={preview.previewUrl}
          onLoad={preview.handleIframeLoad}
          regions={commentRegions}
          onDrawRegion={editable ? onDrawCommentRegion : undefined}
          onRegionClick={onCommentRegionClick}
        />
      ) : (
        <div
          ref={editorRootRef}
          className={clsx('tiptap cara-paged-editor', bare ? 'flex-grow py-3' : 'p-4')}
          data-editor-scope={EDITOR_SCOPE_ID}
          data-paged={pageCount > 0 ? 'true' : undefined}
          data-overflowing={
            declaredPageCount > 0 && pageCount > declaredPageCount ? 'true' : undefined
          }
          style={
            {
              '--cara-page-count': pageCount,
              '--cara-page-width': pageDimensions(furniture?.size).width,
              '--cara-page-height': pageDimensions(furniture?.size).height,
              ...(furniture
                ? {
                    '--cara-margin-top': `${furniture.margins.topIn}in`,
                    '--cara-margin-right': `${furniture.margins.rightIn}in`,
                    '--cara-margin-bottom': `${furniture.margins.bottomIn}in`,
                    '--cara-margin-left': `${furniture.margins.leftIn}in`,
                    ...(furniture.background ? { '--cara-paper': furniture.background } : {})
                  }
                : {})
            } as React.CSSProperties
          }
        >
          <EditorContent editor={editor} />
        </div>
      )}

      {commentOffer && editor && onCommentSelection && (
        <button
          type="button"
          data-testid="comment-selection"
          className="absolute z-20 rounded-md bg-neutral-900 px-2 py-1 text-xs font-medium text-white shadow-md hover:bg-neutral-700"
          style={{ left: `${commentOffer.x}px`, top: `${commentOffer.y}px` }}
          // The selection would collapse on mousedown, taking the candidate with it.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            const candidate = selectionCandidate(editor as EditorType);
            if (candidate) onCommentSelection(candidate);
            setCommentOffer(null);
          }}
        >
          Comment
        </button>
      )}

      {editable && resizeControlsState?.visible && editor && (
        <div
          style={{
            position: 'fixed',
            left: `${resizeControlsState.x}px`,
            top: `${resizeControlsState.y}px`
          }}
        >
          <ImageResizeControls
            width={resizeControlsState.width}
            height={resizeControlsState.height}
            onResize={(width, height) => {
              editor.commands.setImageSize({ width, height });
              setResizeControlsState(null);
            }}
            onDelete={() => {
              editor.commands.deleteImage();
              setResizeControlsState(null);
            }}
            onClose={() => setResizeControlsState(null)}
          />
        </div>
      )}
    </div>
  );
};

/** @deprecated Use `HtmlDocumentEditor` — kept for existing imports. */
export const Editor = HtmlDocumentEditor;

const PREVIEW_BAR_ACTION =
  'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-primary-600 hover:bg-neutral-100 dark:text-primary-400 dark:hover:bg-white/10';

/**
 * The bar above a document shown as the client sees it: read it, or step into the editor. It
 * holds Edit's height without it, so the pages do not jump when a version stops being editable.
 */
const PreviewBar = (props: { onEdit?: () => void; onOpenLatest?: () => void; note?: string }) => (
  <div className="flex min-h-10 items-center justify-between px-3 py-1.5">
    <span className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
      Pages
    </span>
    {!props.onEdit && props.note && (
      <span role="status" className="px-2 text-sm text-neutral-500 dark:text-neutral-400">
        {props.note}
      </span>
    )}
    {props.onEdit && (
      <button
        type="button"
        onClick={props.onEdit}
        data-testid="edit-document"
        className={PREVIEW_BAR_ACTION}
      >
        <PencilSquareIcon className="size-4" />
        Edit document
      </button>
    )}
    {!props.onEdit && props.onOpenLatest && (
      <button type="button" onClick={props.onOpenLatest} className={PREVIEW_BAR_ACTION}>
        Open latest version
      </button>
    )}
  </div>
);
